import { productsService } from '../products/productsService';
import type { AdminPreOrder, PreOrder, PreOrderInput, PreOrderStatus } from '../../types';
import { delay, mockStorageKeys, readJson, writeJson } from '../mock/storage';

function readPreOrders(): PreOrder[] {
  return readJson<PreOrder[]>(mockStorageKeys.preOrders, []);
}

function savePreOrders(preOrders: PreOrder[]) {
  writeJson(mockStorageKeys.preOrders, preOrders);
}

function toAdminPreOrder(preOrder: PreOrder): AdminPreOrder {
  return {
    ...preOrder,
    customerName: preOrder.contact.name,
    customerEmail: preOrder.contact.email,
  };
}

export async function mockFetchPreOrders(): Promise<PreOrder[]> {
  await delay(200);
  return readPreOrders();
}

export async function mockCreatePreOrder(input: PreOrderInput): Promise<PreOrder> {
  await delay();

  const product = await productsService.getProduct(input.productId);
  const quantity = Math.max(1, Math.floor(input.quantity));

  const existing = readPreOrders();
  if (
    existing.some(
      (item) => item.productId === product.id && item.status === 'pending',
    )
  ) {
    throw new Error('Bu ürün için zaten bekleyen bir ön siparişiniz var.');
  }

  const preOrder: PreOrder = {
    id: crypto.randomUUID(),
    productId: product.id,
    productName: product.name,
    productSlug: product.slug,
    quantity,
    contact: {
      name: input.contact.name.trim(),
      email: input.contact.email.trim().toLowerCase(),
      phone: input.contact.phone.trim(),
    },
    note: input.note?.trim() || undefined,
    status: 'pending',
    createdAt: new Date().toISOString(),
  };

  savePreOrders([preOrder, ...existing]);
  return preOrder;
}

export async function mockFetchAdminPreOrders(
  status?: PreOrderStatus,
): Promise<AdminPreOrder[]> {
  await delay(200);
  let preOrders = readPreOrders();
  if (status) {
    preOrders = preOrders.filter((item) => item.status === status);
  }
  return preOrders.map(toAdminPreOrder);
}

export async function mockUpdateAdminPreOrder(
  id: string,
  status: 'fulfilled' | 'cancelled',
  adminNote?: string | null,
): Promise<AdminPreOrder> {
  await delay();

  const preOrders = readPreOrders();
  const index = preOrders.findIndex((item) => item.id === id);
  if (index === -1) {
    throw new Error('Ön sipariş bulunamadı.');
  }

  if (preOrders[index].status !== 'pending') {
    throw new Error('Yalnızca bekleyen ön siparişler güncellenebilir.');
  }

  const updated: PreOrder = {
    ...preOrders[index],
    status,
    note: adminNote?.trim()
      ? [preOrders[index].note, `Admin: ${adminNote.trim()}`].filter(Boolean).join('\n')
      : preOrders[index].note,
  };

  const next = [...preOrders];
  next[index] = updated;
  savePreOrders(next);
  return toAdminPreOrder(updated);
}
